import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { isAbort } from '../api.ts'

export interface AsyncState<T> {
  data: T | undefined
  error: Error | null
  loading: boolean
  /** Перечитать данные, не сбрасывая показанные. */
  reload: () => void
  setData: Dispatch<SetStateAction<T | undefined>>
}

/** Загрузка данных с отменой при смене зависимостей. keep — не сбрасывать показанные данные при смене зависимостей (поиск). */
export function useAsync<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  opts: { keep?: boolean } = {},
): AsyncState<T> {
  const keep = Boolean(opts.keep)
  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)
  const fnRef = useRef(fn)
  useEffect(() => {
    fnRef.current = fn
  })
  const depsKey = JSON.stringify(deps)
  const lastKey = useRef<string | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    const keyChanged = lastKey.current !== depsKey
    lastKey.current = depsKey
    if (keyChanged) {
      if (!keep) setData(undefined)
      setLoading(true)
    }
    fnRef
      .current(ctrl.signal)
      .then((d) => {
        if (ctrl.signal.aborted) return
        setData(d)
        setError(null)
        setLoading(false)
      })
      .catch((e: unknown) => {
        if (isAbort(e) || ctrl.signal.aborted) return
        setError(e instanceof Error ? e : new Error(String(e)))
        setLoading(false)
      })
    return () => ctrl.abort()
  }, [depsKey, nonce, keep])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { data, error, loading, reload, setData }
}

/** Стабильная функция, вызывающая fn не чаще чем раз в ms (последний вызов выигрывает). */
export function useDebounced(fn: () => void, ms: number): () => void {
  const fnRef = useRef(fn)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => {
    fnRef.current = fn
  })
  useEffect(() => () => window.clearTimeout(timer.current), [])
  return useCallback(() => {
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => fnRef.current(), ms)
  }, [ms])
}

/** Значение с задержкой (для поиска по вводу). */
export function useDebouncedValue<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms)
    return () => window.clearTimeout(t)
  }, [value, ms])
  return v
}

/** Перерисовка раз в ms, пока active (для живых таймеров). */
export function useTicker(ms: number, active = true): number {
  const [n, setN] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const id = window.setInterval(() => setN(Date.now()), ms)
    return () => window.clearInterval(id)
  }, [ms, active])
  return n
}

/** Флаг «идёт отправка» + обёртка, не дающая запустить действие дважды. */
export function useBusy(): [boolean, <R>(p: () => Promise<R>) => Promise<R | undefined>] {
  const [busy, setBusy] = useState(false)
  const ref = useRef(false)
  const run = useCallback(async <R>(p: () => Promise<R>): Promise<R | undefined> => {
    if (ref.current) return undefined
    ref.current = true
    setBusy(true)
    try {
      return await p()
    } finally {
      ref.current = false
      setBusy(false)
    }
  }, [])
  return [busy, run]
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    try {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      ta.remove()
      return ok
    } catch {
      return false
    }
  }
}
